import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DemographicsData } from '../../lib/types';

interface DemographicsChartProps {
  data: DemographicsData;
  className?: string;
}

export function DemographicsChart({ data, className = '' }: DemographicsChartProps) {
  const genderColors = ['#3B82F6', '#EC4899'];
  const ageColors = ['#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  const formatTooltip = (value: number, name: string, props: any) => {
    if (props.payload.percentage) {
      return [`${value.toLocaleString()} (${props.payload.percentage.toFixed(1)}%)`, name];
    }
    return [value.toLocaleString(), name];
  };

  return (
    <div className={`bg-white p-6 rounded-lg shadow ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Demographics</h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gender Distribution */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Gender Distribution</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.gender}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="count"
                >
                  {data.gender.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={genderColors[index % genderColors.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={formatTooltip} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center space-x-4 text-sm">
            {data.gender.map((item, index) => (
              <div key={item.gender} className="flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2" 
                  style={{ backgroundColor: genderColors[index] }}
                />
                <span className="capitalize">{item.gender}: {item.percentage.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Age Ranges */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Age Distribution</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.ageRanges}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="ageRange" 
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip formatter={formatTooltip} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {data.ageRanges.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={ageColors[index % ageColors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
